import { useMemo, useState } from 'react'
import { Key } from 'react-aria'
import { ObjectProperties, ObjectProperty, ObjectPropertyValue } from '../../shared/types'
import { LayoutElementComponent } from '../layouts/types'
import { ChildObject } from './child-object'
import { CloudinaryInput } from './cloudinary-input'
import { DatePicker } from './date-picker'
import { FileInput, Input } from './input'
import { MultiSelect } from './multi-select'
import { PluginInput } from './plugin-input'
import { Options, Select } from './select'
import { Switch } from './switch'
import { Textarea } from './textarea'
import { Tooltip, TooltipWrap } from './tooltip'

type ObjectPropertyInputProps = Omit<ObjectProperty, 'id' | 'object' | 'type'> & {
	value?: ObjectPropertyValue
	type: LayoutElementComponent['type']
	inputProps?: any // eslint-disable-line @typescript-eslint/no-explicit-any
	data?: ObjectProperties
	defaultValue?: ObjectPropertyValue
}

const getLabel = (label: string, description?: string) => {
	if (!description) return label
	return (
		<Tooltip delay={25} content={description}>
			<TooltipWrap>
				<span className="underline decoration-dotted cursor-help">{label}</span>
			</TooltipWrap>
		</Tooltip>
	)
}

export function ObjectPropertyField({
	description,
	name,
	identifier,
	type,
	options,
	value,
	isEditable = true,
	isPositive,
	isRequired,
	referenceTo,
	inputProps,
	data,
	defaultValue,
}: ObjectPropertyInputProps) {
	const label = getLabel(name, description)
	const selectOptions = useMemo(() => {
		return options?.map(option => ({ value: option.id as string, label: option.name }))
	}, [options])

	const effectiveValue = value ?? defaultValue

	const [selectedKeys, setSelectedKeys] = useState<Array<Key>>(
		(effectiveValue ?? []) as Array<Key>,
	)

	switch (type) {
		case 'hidden':
			return (
				<input
					type="hidden"
					name={identifier}
					value={effectiveValue as string | undefined}
				/>
			)
		case 'string':
			return (
				<Input
					label={label}
					name={identifier}
					maxLength={255}
					isReadOnly={!isEditable}
					defaultValue={effectiveValue as string | undefined}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'text':
			return (
				<Textarea
					label={label}
					name={identifier}
					className="col-span-2"
					isReadOnly={!isEditable}
					defaultValue={effectiveValue as string | undefined}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'number':
		case 'currency':
			return (
				<Input
					label={label}
					name={identifier}
					type="number"
					isReadOnly={!isEditable}
					defaultValue={effectiveValue as string | undefined}
					min={isPositive ? 0 : undefined}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'file':
			return (
				<FileInput
					label={label}
					name={identifier}
					defaultValue={effectiveValue as string | undefined}
					isReadOnly={!isEditable && !!effectiveValue}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'single':
			return (
				<Select
					label={label}
					name={identifier}
					span="FULL"
					isDisabled={!isEditable}
					defaultSelectedKey={effectiveValue as string | undefined}
					options={selectOptions as Options}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'multi':
			return (
				<MultiSelect
					label={label}
					name={identifier}
					selectedKeys={selectedKeys}
					isDisabled={!isEditable}
					options={selectOptions as Options}
					onSelectionChange={setSelectedKeys}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'datetime':
			return (
				<DatePicker
					label={label}
					name={identifier}
					defaultValue={effectiveValue}
					isRequired={isRequired}
					{...inputProps}
					isDisabled={!isEditable}
				/>
			)
		case 'boolean':
			return (
				<Switch
					label={label}
					name={identifier}
					defaultSelected={effectiveValue as boolean}
					{...inputProps}
				/>
			)
		case 'color':
			return (
				<Input
					label={label}
					name={identifier}
					type="color"
					defaultValue={effectiveValue}
					isReadOnly={!isEditable}
					isRequired={isRequired}
					{...inputProps}
				/>
			)
		case 'reference': {
			if (!referenceTo) return null
			return (
				<ChildObject
					name={name}
					identifier={identifier}
					selectedKey={selectedKeys?.[0] ?? null}
					onSelectionChange={key => setSelectedKeys(key ? [key] : [])}
					object={referenceTo}
					isRequired={isRequired}
				/>
			)
		}
		case 'plugin':
			return (
				<PluginInput
					label={label}
					name={identifier}
					aria-label={label ?? name}
					{...inputProps}
					data={data}
				/>
			)
		case 'cloudinary':
			return (
				<CloudinaryInput
					label={label}
					name={identifier}
					value={effectiveValue as string | undefined}
					width={160}
					height={90}
					{...inputProps}
				/>
			)
		case 'derived':
			return effectiveValue
	}
}
